/**
 * Excel serial day-count ⇄ ISO `YYYY-MM-DD` conversion.
 *
 * A date-formatted cell in an .xlsx file stores its value as a serial
 * day-count, not as text. Two workbook date systems exist and a workbook
 * declares which one it uses (`<workbookPr date1904="1"/>`):
 *
 *   - 1900 system (the default on Windows): serial 1 = 1900-01-01, with
 *     Excel's well-known phantom 1900-02-29 leap-year bug. The constant 25569
 *     is the day offset between Excel's effective epoch (1899-12-30) and the
 *     Unix epoch (1970-01-01); it already absorbs the phantom day for every
 *     real calendar date (those on/after 1900-03-01), which is every date an
 *     accounting record carries.
 *   - 1904 system (the historical Mac default): serial 0 = 1904-01-01. A 1904
 *     serial `N` denotes the same calendar day as the 1900 serial `N + 1462`
 *     (1462 = the number of days from 1899-12-30 to 1904-01-01), so we reuse
 *     the 1900 path after shifting.
 *
 * Pure functions, no `Date.now()` / argless `new Date()` — referentially
 * transparent and safe under bun's shared-global test runner.
 */

/** Day offset between Excel's 1900-system epoch (1899-12-30) and 1970-01-01. */
const UNIX_EPOCH_SERIAL_1900 = 25569;
/** Days from the 1900-system epoch to 1904-01-01 (the 1904-system epoch). */
const DATE_1904_OFFSET = 1462;
const MS_PER_DAY = 86_400_000;

export interface SerialDateOptions {
  /** Workbook uses the 1904 date system (`<workbookPr date1904="1"/>`). */
  date1904?: boolean;
}

/**
 * Convert an Excel serial day-count to an ISO `YYYY-MM-DD` string.
 *
 * Only the integer (whole-day) part is used; a fractional time-of-day
 * component is truncated, since a calendar date is all a date cell carries for
 * import purposes.
 */
export function excelSerialToIsoDate(
  serial: number,
  options: SerialDateOptions = {},
): string {
  const shifted = options.date1904 ? serial + DATE_1904_OFFSET : serial;
  const wholeDays = Math.floor(shifted);
  const ms = (wholeDays - UNIX_EPOCH_SERIAL_1900) * MS_PER_DAY;
  const d = new Date(ms);
  const yyyy = String(d.getUTCFullYear()).padStart(4, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Convert an ISO `YYYY-MM-DD` string to an Excel serial day-count — the exact
 * inverse of {@link excelSerialToIsoDate} for whole dates. Returns `null` for
 * a value that is not a real ISO date.
 */
export function isoDateToExcelSerial(
  iso: string,
  options: SerialDateOptions = {},
): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const ms = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ms)) return null;
  // Reject values that round-trip to a different date (e.g. 2026-02-30, which
  // Date.parse would silently roll forward).
  const check = new Date(ms);
  const roundtrip = `${String(check.getUTCFullYear()).padStart(4, '0')}-${String(
    check.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(check.getUTCDate()).padStart(2, '0')}`;
  if (roundtrip !== iso) return null;
  const serial1900 = Math.round(ms / MS_PER_DAY) + UNIX_EPOCH_SERIAL_1900;
  return options.date1904 ? serial1900 - DATE_1904_OFFSET : serial1900;
}
